import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Permissao } from '../models/enums/permissao';
import { LoginResponse } from './models/login-response';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly storageKey = {
    login: 'login',
    sessionActive: 'sessionActive',
  };

  private _token: string | null = null;
  private loggedIn = new BehaviorSubject<boolean>(!!localStorage.getItem('sessionActive'));

  get isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  get token(): string | null {
    return this._token;
  }

  getLogin(): string | null {
    return localStorage.getItem(this.storageKey.login);
  }

  constructor(private http: HttpClient) {}

  // Chamado pelo APP_INITIALIZER antes do roteador iniciar.
  // Tenta restaurar a sessão via cookie HttpOnly. Se o cookie estiver expirado ou
  // inválido (401/403), limpa o estado. Erros de rede são ignorados para não
  // bloquear o app quando o servidor estiver temporariamente indisponível.
  initializeAuth(): Observable<void> {
    if (!localStorage.getItem(this.storageKey.sessionActive)) {
      return of(void 0);
    }

    return this.refreshAccessToken().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 403) {
          localStorage.removeItem(this.storageKey.sessionActive);
          this.loggedIn.next(false);
        }
        return of(void 0);
      }),
      map(() => void 0)
    );
  }

  login(login: string, senha: string): Observable<void> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { login, senha }, {
      withCredentials: true
    }).pipe(
      tap(response => {
        this._token = response.accessToken;
        localStorage.setItem(this.storageKey.login, login);
        localStorage.setItem(this.storageKey.sessionActive, '1');
        this.loggedIn.next(true);
      }),
      map(() => void 0)
    );
  }

  refreshAccessToken(): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/refresh`, {}, {
      withCredentials: true
    }).pipe(
      tap(response => {
        this._token = response.accessToken;
        localStorage.setItem(this.storageKey.sessionActive, '1');
        this.loggedIn.next(true);
      })
    );
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, null, {
      withCredentials: true
    }).subscribe({
      error: () => { /* falha silenciosa */ }
    });

    this._token = null;
    localStorage.removeItem(this.storageKey.login);
    localStorage.removeItem(this.storageKey.sessionActive);
    this.loggedIn.next(false);
  }

  isAuthorized(permissao?: Permissao): boolean {
    if (!this.token) return false;
    if (permissao === undefined) return true;

    return this.getPermissoesFromToken().includes(permissao);
  }

  // Decodifica o payload do JWT apenas para uso de UX (ex: checar permissões no guard/menu).
  // A validação real da assinatura e das claims é sempre feita pelo servidor via [Authorize(Policy)].
  private getPermissoesFromToken(): string[] {
    const token = this.token;
    if (!token) return [];

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return [];

      // JWT usa base64url — difere do base64 padrão: substitui - → + e _ → /,
      // e adiciona padding = para que atob() processe corretamente.
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

      const payload = JSON.parse(atob(padded));
      const claim: unknown = payload['permissao'];
      if (!claim) return [];
      return Array.isArray(claim) ? claim as string[] : [claim as string];
    } catch {
      return [];
    }
  }
}
